import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { useAppSelector } from '@store/index';
import { TailwindSpinner } from '@components/common/Elements/Spinner';
import { useSwrJobAudioInterview } from '@hooks/useSwrApi/endpoints/job';
import {PhoneIcon} from "@heroicons/react/24/solid";
import {PulsatingBestieAvatar} from "@components/templates/audio-interview/partials/PulsatingBestieAvatar";

interface AudioInterviewJobV2Props {
  slug: string;
  locale: Locale;
  scope?: 'job';
  onComplete?: () => void;
}

const AudioInterviewJobV2: React.FC<AudioInterviewJobV2Props> = ({
  slug,
  locale,
  scope = 'job',
  onComplete,
}) => {
  // Explicitly type t as accepting string keys
  const { t } = useTranslation('common') as { t: (key: string, options?: any) => string };
  const userResume = useAppSelector((state) => state.userOwnResume.userResume);
  // Audio permissions are not needed for this modal; call is made on device.
  const {
    trigger: triggerAudioInterview,
    isMutating: isStarting,
    error: startError,
  } = useSwrJobAudioInterview({ slug, locale }); // remove toastsOptions
  const [connectionState, setConnectionState] = useState<'idle' | 'connecting' | 'connected'>('idle');
  const [localError, setLocalError] = useState<string | null>(null);
  const hasDisconnectedRef = useRef(false);

  const startInterview = async () => {
    hasDisconnectedRef.current = false;
    setLocalError(null);
    setConnectionState('connecting');
    try {
      const response = await triggerAudioInterview();
      if (response && typeof onComplete === 'function') {
        onComplete();
        setConnectionState('idle');
      }
    } catch (e) {
      // If error occurs, connectionState is handled by useEffect or here
      setConnectionState('idle');
    }
  };

  const handleStartInterview = async () => {
    if (scope === 'job' && !slug) return;
    await startInterview();
  };

  useEffect(() => {
    if (startError && connectionState === 'connecting') {
      // V1 API error structure: { errors: [{ message: string, ... }] }
      const message = startError?.errors?.[0]?.message || t('audio-interview.job-landing.error');
      setLocalError(message);
      setConnectionState('idle');
    }
  }, [startError, connectionState, t]);

  // Interview Room (connecting/connected)
  if ((connectionState === 'connecting' || connectionState === 'connected') && !hasDisconnectedRef.current) {
    // Show loading spinner while starting the call
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <TailwindSpinner className="size-8" />
        <p className="mt-4 text-center text-lg font-medium text-ink">{t('audio-interview.job-landing.cta-loading')}</p>
      </div>
    );
  }



  // Main Landing UI (Design inspired by provided HTML, using Bestie asset)
  return (
    <div className="w-full max-w-3xl mx-auto text-center space-y-12">
      <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">
        {t('audio-interview.job-landing.title' as string)}
      </h1>
      <div className="flex justify-center">
        <div className="relative">
          <div className="flex flex-1 flex-col items-center justify-center">
            <PulsatingBestieAvatar
              isConnected={false}
            />
          </div>
        </div>
      </div>
      <div className="bg-surface-50 p-6 md:p-8 rounded-3xl shadow-sm border border-gray-100 max-w-2xl mx-auto flex flex-col md:flex-row items-center gap-6 text-left">
        <div>
          <p className="text-lg md:text-xl font-medium leading-relaxed">
            {t('audio-interview.job-landing.info' as string)}
          </p>
        </div>
      </div>
      <div className="pt-4">
        <button
          className="bg-secondary hover:bg-secondary-dark text-white font-bold py-5 px-12 rounded-full text-xl transition-all shadow-lg hover:shadow-xl active:scale-95 flex items-center gap-3 mx-auto disabled:opacity-60 disabled:cursor-not-allowed"
          onClick={handleStartInterview}
          disabled={isStarting}
        >
          <PhoneIcon className="size-5" />
          {isStarting ? t('audio-interview.job-landing.cta-loading' as string, { defaultValue: 'Starting...' }) : t('audio-interview.job-landing.cta' as string)}
        </button>
        {localError && (
          <div className="text-red-600 text-sm font-medium pt-4 pb-2">
            {localError}
          </div>
        )}
      </div>
      {/* InterviewTypeSwitcher is rendered by parent, do not duplicate */}
    </div>
  );
};

export default AudioInterviewJobV2;
